import { NgClass } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Preferences } from '@capacitor/preferences';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { StatusBar } from '@capacitor/status-bar';
import { TranslateService } from '@ngx-translate/core';
import { Message, PrimeNGConfig } from 'primeng/api';
import { MessagesModule } from 'primeng/messages';
import { map, Subject, switchMap, take, takeUntil, timer } from 'rxjs';
import { environment } from '../environments/environment';
import { FloatingChatComponent } from './core/components/floating-chat/floating-chat.component';
import { FooterComponent } from './core/components/footer/footer.component';
import { HeaderComponent } from './core/components/header/header.component';
import { SpinnerComponent } from './core/components/spinner/spinner.component';
import { ModalFullScreenComponent } from './core/modals/modal-full-screen/modal-full-screen.component';
import { HeaderFooterService } from './core/services/header-footer.service';
import { InactivityService } from './core/services/inactivity.service';
import { Devices } from './shared/enums/devices.enum';
import { StorageKeys } from './shared/enums/storage-keys.enum';
import { UrlKeys } from './shared/enums/urls.enum';
import { AuthService } from './shared/services/auth.service';
import { ConsoleService } from './shared/services/console.service';
import { GoogleMapsService } from './shared/services/google-maps.service';
import { LoadingService } from './shared/services/loading.service';
import { MessageService } from './shared/services/message.service';
import { ModalService } from './shared/services/modal.service';
import { StorageDataService } from './shared/services/storage-data.service';
import { StorageService } from './shared/services/storage.service';
import { UtilsService } from './shared/services/utils.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    FloatingChatComponent,
    FooterComponent,
    HeaderComponent,
    MessagesModule,
    NgClass,
    RouterOutlet,
    SpinnerComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private _authService = inject(AuthService);
  private _consoleService = inject(ConsoleService);
  private _headerFooterService = inject(HeaderFooterService);
  private _inactivityService = inject(InactivityService);
  private _loadingService = inject(LoadingService);
  private _messageService = inject(MessageService);
  private _metaService = inject(Meta);
  private _modalService = inject(ModalService);
  private _primeNGConfig = inject(PrimeNGConfig);
  private _router = inject(Router);
  private _storageDataService = inject(StorageDataService);
  private _storageService = inject(StorageService);
  private _translateService = inject(TranslateService);
  private _utilsService = inject(UtilsService);
  private _googleMapsService = inject(GoogleMapsService);

  private readonly destroySubject$ = new Subject<void>();
  title = 'cigna_CP';
  showHeader!: boolean;
  showFooter!: boolean;
  showLoading!: boolean;
  messages: Message[] = [];
  enableMessageService: boolean = true;
  closableMessage: boolean = true;
  isLogged: boolean = false;
  isAdmin: boolean = false;
  isChatHostPage: boolean = false;
  statusBarHidden: boolean = false;

  async ngOnInit(): Promise<void> {
    this.initTranslate();
    this.subscribeServices();
    this.routerSubscribe();
    this.avoidIphoneAutoScale();
    this.authSubscribe();

    if (this._utilsService.isApp()) {
      this.checkAppVersionAndMaintenance();
      this.appConfiguration();
    }
    this._inactivityService.init();
    const lang = this._storageService.localStorageGetItem(StorageKeys.LANGUAGE) || 'es';
    await this._googleMapsService.loadGoogleMapsScript(lang);
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
  }

  checkAppVersionAndMaintenance(): void {
    if (this._utilsService.isApp()) {
      this._utilsService
        .correctAppVersionAndMaintenance()
        .pipe(take(1))
        .subscribe(res => {
          let modal: string = '';
          if (res.maintenance) {
            modal = 'maintenance-app';
          } else if (!res.correctAppVersion && environment.checkOutdated) {
            modal = 'outdated';
          }
          if (modal) {
            let width = '45rem';
            if (this._utilsService.isMobile() || this._utilsService.isTablet()) {
              width = '95%';
            }
            this._modalService.showModal(
              ModalFullScreenComponent,
              { modal: modal, appInfo: res.appInfo },
              width,
              true,
              false
            );
          }
        });
    }
  }

  initTranslate(): void {
    let lang: string = environment.defaultLang;
    if (this._storageService.localStorageGetItem(StorageKeys.LANGUAGE)) {
      lang = this._storageService.localStorageGetItem(StorageKeys.LANGUAGE);
    } else {
      this._storageService.localStorageSetItem(StorageKeys.LANGUAGE, lang);
    }
    this._translateService.use(lang);
    document.documentElement.lang = lang;
    this._translateService
      .get('primeng')
      .subscribe(res => this._primeNGConfig.setTranslation(res));
  }

  subscribeServices(): void {
    this._headerFooterService.showHeader$.pipe(takeUntil(this.destroySubject$)).subscribe(show => {
      setTimeout(() => (this.showHeader = show));
    });
    this._headerFooterService.showFooter$.pipe(takeUntil(this.destroySubject$)).subscribe(show => {
      setTimeout(() => (this.showFooter = show));
    });
    // switchMap cancels a pending apply so a late hide cannot overwrite a newer show
    // (the previous bare setTimeout raced with iOS chat holdLoader).
    this._loadingService.showLoader$
      .pipe(
        takeUntil(this.destroySubject$),
        switchMap(load => timer(0).pipe(map(() => load)))
      )
      .subscribe(load => {
        this.showLoading = load;
      });
    this._messageService.messageData$.pipe(takeUntil(this.destroySubject$)).subscribe(message => {
      setTimeout(() => {
        if (message[0]?.detail) {
          message[0].detail = this._translateService.instant(message[0].detail as string, message[0].data);
          this.messages = message;
        }
      });
    });
  }

  routerSubscribe(): void {
    this._router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.cleanMessages();
        this.isChatHostPage = this._router.url.includes(UrlKeys.CHAT_HOST);
        if (this._router.url.indexOf(UrlKeys.MI_CIGNA) === -1) {
          this._storageDataService.cleanAuthorized();
        }
        this._metaService.removeTag('name=description');
      }
      //menu active
      //telemedicina
      if (
        this._router.url.includes(UrlKeys.TELEMEDICINA) ||
        this._router.url.includes(UrlKeys.DR_CIGNA) ||
        this._router.url.includes(UrlKeys.TELEDERMA)
      ) {
        this._headerFooterService.menuOptionSelected$.next('telemedicina');
      }
      //gestiones
      else if (
        this._router.url.includes(UrlKeys.CUADRO_MEDICO) ||
        this._router.url.includes(UrlKeys.REEMBOLSOS) ||
        this._router.url.includes(UrlKeys.AUTORIZACIONES)
      ) {
        this._headerFooterService.menuOptionSelected$.next('gestiones');
      }
      //soporte
      else if (this._router.url.includes(UrlKeys.SOPORTE)) {
        this._headerFooterService.menuOptionSelected$.next('soporte');
      }
      //mas
      else if (this._router.url.includes(UrlKeys.MAS_SALUD)) {
        this._headerFooterService.menuOptionSelected$.next('mas');
      }
      //quotation tool
      else if (this._router.url.includes(UrlKeys.QUOTATION_TOOL)) {
        this._metaService.updateTag({
          name: 'description',
          content:
            'Calcula tu seguro de salud con nosotros  al mejor precio en menos de 1 minuto y consigue la mejor opción de seguro de salud según tus necesidades y las de tu familia.',
        });
      } //otro
      else {
        this._headerFooterService.menuOptionSelected$.next('');
      }
    });
  }

  avoidIphoneAutoScale(): void {
    if (navigator.userAgent.indexOf('iPhone') > -1) {
      document
        .querySelector('[name=viewport]')
        ?.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
    }
  }

  authSubscribe(): void {
    this._authService.logged$.subscribe((val: boolean) => {
      this.isLogged = val;
      this.isAdmin = this._storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA).isAdmin;
      const embeddedServiceHelpButton: any = document.getElementsByClassName(
        'embeddedServiceHelpButton'
      );
      if (!this.isLogged || !!this.isAdmin) {
        if (embeddedServiceHelpButton.length) {
          embeddedServiceHelpButton[0].style.setProperty('display', 'none');
        }
      } else {
        if (embeddedServiceHelpButton.length) {
          embeddedServiceHelpButton[0].style.setProperty('display', 'block');
        }
      }
    });
  }

  appConfiguration(): void {
    if (!this._utilsService.isIOS()) {
      Preferences.get({ key: 'com.cigna.mobile.micigna.spain.install' }).then((key: any) => {
        this._consoleService.log('[appConfiguration] Preferences', key.value);
        if (!key.value) {
          this._storageService.localStorageRemoveItem(StorageKeys.BIOMETRIA);
          Preferences.set({
            key: 'com.cigna.mobile.micigna.spain.install',
            value: 'true',
          }).then();
        }
      });
    }
    if (this._utilsService.getDevice() === Devices.MOBILE) {
      ScreenOrientation.lock({ orientation: 'portrait' }).then();
    }
    StatusBar.getInfo().then(info => {
      this._consoleService.log('[appConfiguration] hideAppStatusBar', info);
      if (info.visible) {
        StatusBar.setBackgroundColor({ color: '#ffffff' }).then();
        StatusBar.hide().then(() =>
          StatusBar.getInfo().then(info2 => {
            this._consoleService.log('[appConfiguration] hideAppStatusBar', info2);
            if (!info2.visible) {
              this.statusBarHidden = true;
            }
          })
        );
      }
    });
  }

  cleanMessages(): void {
    this.messages = [];
  }

  onActivate(): void {
    document.body.style.overflow = 'auto';
    this._utilsService.scrollTop();
  }
}
